import capitalize from '@liveauctioneers/caterwaul-components/lib/utils/capitalize';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Tabs } from '@liveauctioneers/hammer-ui-core/tabs';
import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import CategorySearchCard from '@/components/GridItemCard/Card/CardOptions/CategorySearchCards/CategorySearchCard';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import PageTitle from '@/components/PageTitle/PageTitle';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { PAGE_ITEMS_TO_VIEW } from '@/components/Pagination/helpers/filterSortOptions';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import { useAppSelector } from '@/redux/hooks';
import { getDiscoverItemIds, getDiscoverPaginationRecordsCount } from '@/redux/modules/discover';
import { getSaveSearchParams } from '@/redux/modules/saveSearch';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import { parseQueryParams } from '@/utils/queryParams';
import { safelyRender } from '@/utils/safelyRender';
import { buildSearchQuery } from '@/utils/search/searchQuery';
import { toQueryString } from '@/utils/urls';

import DiscoverPageHeader from './DiscoverPageHeader';
import DiscoverPageHeaderMeta from './DiscoverPageHeaderMeta';
import normalizeQuery from './helpers/normalizeQuery';

const DEFAULT_PAGE_SIZE = 24;

type RouteParams = {
    topic: string;
};

interface QueryParams {
    page?: number;
    pageSize?: number;
    status?: string;
}

const DiscoverPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { pathname, search } = location;
    const { topic } = useParams<RouteParams>();
    const itemIds = useAppSelector(getDiscoverItemIds);
    const totalRecords = useAppSelector(getDiscoverPaginationRecordsCount);
    const topicString = topic.split('-').join(' ');
    const queryParams: QueryParams = parseQueryParams(search);
    const { pageSize } = queryParams;
    const searchQuery = buildSearchQuery({
        pathname,
        queryParams: { ...queryParams, keyword: topicString },
        searchPageSize: pageSize ? Number(pageSize) : 24,
    });

    const { status = 'upcoming' } = queryParams;

    const navigateTo = useCallback(
        (param: QueryParams) => {
            const newParams = normalizeQuery({ ...queryParams, ...param });
            navigate({ search: toQueryString(newParams) });
        },
        [navigate, queryParams]
    );

    const handleTabChange = useCallback(
        (name: string) => {
            navigateTo({ status: name === 'archive' ? 'archive' : '' });
        },
        [navigateTo]
    );

    const { distance, publishDate, ranges } = searchQuery;
    const saveSearchParams = getSaveSearchParams({ keyword: topicString });
    const allSearchParams: SearchParamsType = {
        ...saveSearchParams,
        distance,
        publishDate,
        ranges,
    };

    const content = (
        <>
            <DiscoverPageHeader />
            <FluidItemGrid
                itemCardOption={CategorySearchCard}
                itemIds={itemIds}
            />
            {totalRecords > DEFAULT_PAGE_SIZE && (
                <Pagination
                    onChange={navigate}
                    pageSizeOptions={PAGE_ITEMS_TO_VIEW}
                    paginationFilter={{
                        page: queryParams.page || 1,
                        pageSize: queryParams.pageSize || DEFAULT_PAGE_SIZE,
                        totalRecords,
                    }}
                />
            )}
        </>
    );

    return (
        <StyledPageContainer>
            <DiscoverPageHeaderMeta
                firstItemId={itemIds?.[0]}
                title={topicString}
            />
            <PageTitle
                title={
                    <RowDiv>
                        {safelyRender({
                            html: capitalize(topicString),
                            wrapper: 'span',
                        })}
                        <StyledSaveSearchContainer>
                            <SaveSearchContainer
                                allSearchParams={allSearchParams}
                                saveSearchParams={saveSearchParams}
                                small
                            />
                        </StyledSaveSearchContainer>
                    </RowDiv>
                }
            />
            <Tabs
                className="[&_.hui-tab-header-row]:justify-end tabletMax:[&_.hui-tab-header-row]:justify-start"
                currentTab={status}
                onTabChanged={handleTabChange}
                tabs={[
                    {
                        content,
                        contentId: 'upcoming',
                        label: <FormattedMessage id="upcoming" />,
                    },
                    {
                        content,
                        contentId: 'archive',
                        label: <FormattedMessage id="price_results" />,
                    },
                ]}
            />
        </StyledPageContainer>
    );
};

export default DiscoverPage;

const StyledPageContainer = styled.div`
    width: 100%;
    margin: 0 auto;
    padding: 0 20px 30px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
`;

const StyledSaveSearchContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-left: 20px;
    min-width: 180px;
    min-height: 28px;
`;

const RowDiv = styled.div`
    display: flex;
`;
